import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';

import { type BaseParams, type CreateEmojiGroup, type UpdateEmojiGroup } from '@app/client';
import {
    callCheckEmoji,
    callCreateEmojiGroup,
    callDeleteEmojiGroup,
    callGetEmojiGroups,
    callUpdateEmojiGroup,
} from '@app/client/resources/admin/emoji-groups';

import { getNextPageParam, getPagesPagingParams } from '../../deprecated';
import { useQueryInvalidation } from '../../reactQuery';

const EMOJI_GROUP_CACHE = ['emoji_groups'];

export function useGetEmojiGroups() {
    const { data, ...rest } = useInfiniteQuery(
        EMOJI_GROUP_CACHE,
        ({ pageParam = 0, signal }) => callGetEmojiGroups({ settings: { signal }, query: { page: pageParam } }),
        { retry: false, keepPreviousData: true, getNextPageParam },
    );
    const pagingParams = getPagesPagingParams(data?.pages);
    const groups = data?.pages[pagingParams?.page || 0]?.data;

    return {
        data,
        pagingParams,
        groups,
        ...rest,
    };
}

export function useCheckEmoji(eid: string, disabled = false) {
    return useQuery(['check_emoji', eid], ({ signal }) => callCheckEmoji({ eid, settings: { signal } }), {
        enabled: !disabled && !!eid?.length,
        select: (r) => {
            if (r?.ok && r.value) {
                return r.value;
            }
        },
    });
}

export function useMutateEmojiGroup() {
    const invalidateGroups = useQueryInvalidation(EMOJI_GROUP_CACHE);
    const { mutateAsync: deleteGroup } = useMutation(({ id }: { id: string }) => callDeleteEmojiGroup({ id }), {
        onSuccess: invalidateGroups,
    });
    const { mutateAsync: updateGroup } = useMutation(
        ({ id, value }: { id: string; value: UpdateEmojiGroup }) => callUpdateEmojiGroup({ id, value }),
        {
            onSuccess: invalidateGroups,
        },
    );
    const { mutateAsync: createGroup } = useMutation(
        ({ prefetch, value }: { value: CreateEmojiGroup; prefetch?: BaseParams['prefetch'] }) =>
            callCreateEmojiGroup({ value, prefetch }),
        {
            onSuccess: invalidateGroups,
        },
    );
    return { deleteGroup, updateGroup, createGroup };
}
